import { useTranslation } from '@mint-lib/i18n';
import { Portfolio, UsersAccess } from '@mint-lib/icons';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  typographyVariants,
} from '@mint-lib/tw-components';
import type { KnowledgeBase } from '@mint-sdk/api-ai-assistant';

import { toUserName } from '../../../helpers/knowledge-base/userName.js';
import { InvitedUser } from './InvitedUser.jsx';

type Props = {
  folder?: KnowledgeBase.Folder;
  isLoading: boolean;
};

const SharedWithPopover = ({ folder, isLoading }: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  if (isLoading || !folder) {
    return <Skeleton className="size-5" />;
  }

  return (
    <Popover>
      <PopoverTrigger aria-label={t('Shared with')}>
        <UsersAccess className="cursor-pointer text-tertiary-main" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="left"
        className="z-popover flex max-h-80 w-64 flex-col gap-1 overflow-y-auto p-3"
      >
        <p className={typographyVariants({ variant: 'h6' })}>
          {t('Shared with')}
        </p>
        <SharedList folder={folder} />
      </PopoverContent>
    </Popover>
  );
};

const SharedList = ({ folder }: { folder: KnowledgeBase.Folder }) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  if (folder.allWorkspace) {
    return (
      <div className="flex flex-row items-center gap-1">
        <Portfolio className="fill-text-primary" />
        <p className={typographyVariants({ variant: 'bodyLong01' })}>
          {t('Workspace')}
        </p>
      </div>
    );
  }

  const grantees = folder.grantedUsers ?? [];

  if (!grantees.length) {
    return (
      <p
        className={typographyVariants({
          variant: 'bodyLong01',
          color: 'secondary',
        })}
      >
        {t('No one invited yet')}
      </p>
    );
  }

  return (
    <ul className="flex flex-col" data-testid="shared-with-users">
      {grantees.map((user, index) => (
        <InvitedUser
          key={user.userId ?? `unknown-${index}`}
          firstName={user.firstName}
          lastName={user.lastName}
          label={toUserName(user) || user.email || t('Unknown user')}
        />
      ))}
    </ul>
  );
};

export { SharedWithPopover };
